import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AccountData } from './useAccount'

type Props = {
  account: AccountData
  isMutating: boolean
  onSave: (payload: Partial<AccountData>) => void
}

export function GuardSettingsSection({ account, isMutating, onSave }: Props) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const [localGuardOnline, setLocalGuardOnline] = useState(account.guard_online ?? false)
  const [localGuardSync, setLocalGuardSync] = useState(account.guard_sync ?? false)
  const [localGuardReward, setLocalGuardReward] = useState(account.guard_reward ?? false)
  const [localGuardHeartbeat, setLocalGuardHeartbeat] = useState(account.guard_heartbeat ?? false)
  const [localGuardIp, setLocalGuardIp] = useState(account.guard_ip ?? false)
  const [localGuardV2, setLocalGuardV2] = useState(account.guard_v2 ?? false)

  const [localSyncTreshold, setLocalSyncTreshold] = useState(account.sync_treshold ?? 150)
  const [localHeartbeatTreshold, setLocalHeartbeatTreshold] = useState(account.heartbeat_treshold ?? 300)

  function handleSave() {
    onSave({
      guard_online: localGuardOnline,
      guard_sync: localGuardSync,
      guard_reward: localGuardReward,
      guard_heartbeat: localGuardHeartbeat,
      guard_ip: localGuardIp,
      guard_v2: localGuardV2,
      sync_treshold: localSyncTreshold,
      heartbeat_treshold: localHeartbeatTreshold,
    })
  }

  return (
    <>
      <h3 style={{ marginBottom: 14 }}>{t('guard.title')}</h3>

      <div className="grid grid-2" style={{ marginBottom: 20 }}>
        {/* Toggle Switches */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="toggle-switch">
              <input
                id="guard_online"
                type="checkbox"
                role="switch"
                checked={localGuardOnline}
                onChange={(e) => setLocalGuardOnline(e.target.checked)}
              />
              <label htmlFor="guard_online" />
            </div>
            <label htmlFor="guard_online" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }} className="label">
              {t('guard.onlineMonitor')}
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="toggle-switch">
              <input
                id="guard_sync"
                type="checkbox"
                role="switch"
                checked={localGuardSync}
                onChange={(e) => setLocalGuardSync(e.target.checked)}
              />
              <label htmlFor="guard_sync" />
            </div>
            <label htmlFor="guard_sync" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }} className="label">
              {t('guard.syncMonitor')}
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="toggle-switch">
              <input
                id="guard_reward"
                type="checkbox"
                role="switch"
                checked={localGuardReward}
                onChange={(e) => setLocalGuardReward(e.target.checked)}
              />
              <label htmlFor="guard_reward" />
            </div>
            <label htmlFor="guard_reward" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }} className="label">
              {t('guard.rewardScaleMonitor')}
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="toggle-switch">
              <input
                id="guard_heartbeat"
                type="checkbox"
                role="switch"
                checked={localGuardHeartbeat}
                onChange={(e) => setLocalGuardHeartbeat(e.target.checked)}
              />
              <label htmlFor="guard_heartbeat" />
            </div>
            <label htmlFor="guard_heartbeat" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }} className="label">
              {t('guard.heartbeatMonitor')}
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="toggle-switch">
              <input
                id="guard_ip"
                type="checkbox"
                role="switch"
                checked={localGuardIp}
                onChange={(e) => setLocalGuardIp(e.target.checked)}
              />
              <label htmlFor="guard_ip" />
            </div>
            <label htmlFor="guard_ip" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }} className="label">
              {t('guard.listenAddressMonitor')}
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="toggle-switch">
              <input
                id="guard_v2"
                type="checkbox"
                role="switch"
                checked={localGuardV2}
                onChange={(e) => setLocalGuardV2(e.target.checked)}
              />
              <label htmlFor="guard_v2" />
            </div>
            <label htmlFor="guard_v2" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }} className="label">
              {t('guard.v2Algorithm')}
            </label>
          </div>
        </div>

        {/* Threshold Inputs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="label" htmlFor="sync_treshold">
              {t('guard.syncThreshold')}
            </label>
            <input
              id="sync_treshold"
              type="number"
              className="input"
              min={1}
              max={9999}
              disabled={!localGuardSync}
              value={localSyncTreshold}
              onChange={(e) => setLocalSyncTreshold(Number(e.target.value))}
            />
          </div>

          <div>
            <label className="label" htmlFor="heartbeat_treshold">
              {t('guard.heartbeatThreshold')}
            </label>
            <input
              id="heartbeat_treshold"
              type="number"
              className="input"
              min={1}
              max={9999}
              disabled={!localGuardHeartbeat}
              value={localHeartbeatTreshold}
              onChange={(e) => setLocalHeartbeatTreshold(Number(e.target.value))}
            />
          </div>
        </div>
      </div>

      <button type="button" className="btn btn-sm btn-primary" onClick={handleSave} disabled={isMutating}>
        {tc('actions.save')}
      </button>
    </>
  )
}
